/**
 * The MCP Servers Help topic (#939, Feature G). End-user MCP guidance moved from the platform
 * docs site (mcp-platform docs/using/connecting.md and docs/index.md) into the Marketplace.
 */
import { ClaudeCodeScopeTable, ToolPermissionChoices } from "../components/mcp/McpShared";
import { ToolPermissionLabel } from "../components/mcp/ToolPermissionIcon";
import CopyButton from "../components/CopyButton";
import ChatIcon from "../icons/ChatIcon";
import { onSpaLinkClick } from "../lib/link-click";
import {
  MCP_BUG_URL,
  MCP_ONBOARDING_URL,
  MCP_REQUEST_URL,
  MCP_SLACK_LABEL,
  MCP_SLACK_URL,
} from "../lib/mcp-links";
import { routeToHash, type HelpSectionId, type McpHelpSectionId } from "../lib/routing";
import { Section } from "./HelpSection";
import styles from "./HelpPage.module.css";

interface Props {
  initialSection?: McpHelpSectionId;
  onSectionOpen?: (id: HelpSectionId) => void;
  onOpenMcpServers: () => void;
  /**
   * My Connections is on (REACT_APP_MY_CONNECTIONS_ENABLED): the Managing and troubleshooting
   * sections describe and link to it. Off, they read as they did before My Connections existed.
   */
  myConnections?: boolean;
  /** Opens My Connections. */
  onOpenConnections?: () => void;
}

function Ext({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={styles.link}>
      {children}
    </a>
  );
}

export default function McpHelpTopic({
  initialSection, onSectionOpen, onOpenMcpServers, myConnections = false, onOpenConnections,
}: Props) {
  // All collapsed, as in the prototype, unless a link opened one section.
  const open = (id: McpHelpSectionId) => initialSection === id;
  const connectionsLink = (
    <a
      href={routeToHash({ page: "connections" })}
      onClick={onSpaLinkClick(onOpenConnections ?? (() => {}))}
      className={styles.link}
    >
      My Connections
    </a>
  );
  const serversLink = (
    <a href={routeToHash({ page: "mcp" })} onClick={onSpaLinkClick(onOpenMcpServers)} className={styles.link}>
      MCP Servers
    </a>
  );
  const slack = <Ext href={MCP_SLACK_URL}>{MCP_SLACK_LABEL}</Ext>;

  return (
    <>
      <p className={styles.supportBanner}>
        <ChatIcon className={styles.supportBannerIcon} />
        <span>
          Questions or need a hand? Join our <Ext href={MCP_SLACK_URL}>support Slack channel</Ext>.
        </span>
      </p>

      <div className={styles.sections}>
        <Section id="mcp-what" title="What's an MCP server?" defaultOpen={open("mcp-what")} onOpen={onSectionOpen}>
          <p className={styles.pLead}>
            An MCP server connects your AI tool to a company system, such as Jira and Confluence, Snowflake or Datadog, so it
            can look things up and take actions with your own permissions.
          </p>
          <p className={styles.pFlush}>
            Every server on the CAI MCP Platform is a remote server: one HTTPS endpoint, reached with your Cox identity.
            There&rsquo;s nothing to install and no shared API key to get, and the same endpoint works from every client.
            Browse them all on {serversLink}.
          </p>
          <p className={styles.pFlush}>
            Any Cox employee can connect to any server after signing in with Entra. What you can see inside each system
            depends on your own account there.
          </p>
        </Section>

        <Section id="mcp-desktop" title="Connect in Claude Desktop" defaultOpen={open("mcp-desktop")} onOpen={onSectionOpen}>
          <p className={styles.pLead}>
            Connectors are published to the Cox Claude organization by an admin, so there&rsquo;s no URL to paste and no
            config to edit. Connect the ones you want, once each:
          </p>
          <ol className={styles.list}>
            <li>In Claude Desktop, open <strong>Customize</strong> and choose the <strong>Connectors</strong> tab</li>
            <li>
              Search for the system&rsquo;s name, such as <strong>Rally</strong>. Connector names are often shorter than the
              titles on MCP Servers
            </li>
            <li>Pick the one with the CAI logo and a <strong>Custom</strong> badge</li>
            <li>Click it, then <strong>Connect</strong></li>
            <li>In the browser, sign in if asked, then click <strong>Allow</strong> on &ldquo;Authorize Claude&rdquo;</li>
            <li>
              It now shows under <strong>Yours</strong> with <strong>✓ Connected</strong>. Open it to set its Tool permissions
              (<ToolPermissionChoices />, per tool or per group)
            </li>
          </ol>
          <p className={styles.calloutWarning}>
            <strong>Watch for look-alikes.</strong> Search also shows vendors&rsquo; own public connectors, such as
            &ldquo;Rally MCP&rdquo; by Rally HQ, Inc. Those connect to the vendor directly instead of through the CAI MCP
            Platform. Use the one with the CAI logo.
          </p>
          <p className={styles.pSpaced}>
            The Allow grants Claude access as you; it isn&rsquo;t the service&rsquo;s own credential (see &ldquo;The first
            time you use a server&rdquo; below). Connectors you already have are under <strong>Yours</strong>; the rest are
            under <strong>More you can add</strong>.
          </p>
          <p className={styles.pFlush}>
            <strong>Not listed?</strong> MCP Servers lists everything the platform runs, which isn&rsquo;t the same as
            everything an admin has published to Claude. Ask in {slack} to have it published, or add it to Claude Code
            yourself.
          </p>
          <p className={styles.pFlush}>
            <strong>Different hostname?</strong> A connector&rsquo;s endpoint may use a different platform hostname than MCP
            Servers shows. It reaches the same place; match on the service name in the path, such as{" "}
            <code className={styles.inlineCode}>/rally/mcp</code>.
          </p>
        </Section>

        <Section id="mcp-tool-permissions" title="Tool permissions" defaultOpen={open("mcp-tool-permissions")} onOpen={onSectionOpen}>
          <p className={styles.pLead}>
            Open a connected connector (<strong>Customize → Connectors → Yours</strong>) to see its{" "}
            <strong>Tool permissions</strong>: &ldquo;Choose when Claude is allowed to use these tools.&rdquo; Each tool has
            three settings:
          </p>
          <ul className={styles.list} aria-label="Tool permission settings">
            <li><strong><ToolPermissionLabel kind="allow" /></strong>: Claude uses it without asking</li>
            <li><strong><ToolPermissionLabel kind="ask" /></strong>: Claude asks you each time before using it</li>
            <li><strong><ToolPermissionLabel kind="block" /></strong>: Claude never uses it</li>
          </ul>
          <p className={styles.pSpaced}>
            Tools are grouped (for example, <strong>Read-only tools</strong>), and each group has its own setting that
            applies to every tool in it. A group shows <strong>Custom</strong> when its tools are set differently. That way
            you can connect a server while keeping tools that change data behind a prompt.
          </p>
          <p className={styles.pFlush}>
            You&rsquo;ll only see the tools your organization has given you access to. The connector page is also where you{" "}
            <strong>Disconnect</strong>.
          </p>
        </Section>

        <Section id="mcp-cowork" title="Use it in Cowork" defaultOpen={open("mcp-cowork")} onOpen={onSectionOpen}>
          <p className={styles.pLead}>
            Nothing extra to do. Cowork runs inside Claude Desktop and uses the same connectors. In a new chat, pick{" "}
            <strong>Cowork</strong> with the Chat / Cowork toggle in the composer. Connecting a service once in Desktop makes
            it available in Desktop, Cowork and Claude Code.
          </p>
        </Section>

        <Section id="mcp-claude-code" title="Connect in Claude Code" defaultOpen={open("mcp-claude-code")} onOpen={onSectionOpen}>
          <p className={styles.pLead}>
            <strong>Reuse a connector from your Claude account.</strong> Run <code className={styles.inlineCode}>claude</code>,
            type <code className={styles.inlineCode}>/mcp</code>, and in the claude.ai section expand{" "}
            <strong>Show unused connectors</strong>. Select one; it shows <em>needs authentication</em> until you sign in,
            then <em>connected · N tools</em>.
          </p>
          <p className={styles.pFlush}>
            <strong>Or configure it directly.</strong> Useful if you work in the terminal and don&rsquo;t use Claude Desktop,
            want a specific endpoint, want the setup explicit and reproducible, or want it committed so the whole repo gets
            it:
          </p>
          <CopyButton text="claude mcp add --transport http rally https://mcp.ai.coxautoinc.com/rally/mcp" />
          <ClaudeCodeScopeTable />
          <p className={styles.pFlush}>
            Each server&rsquo;s page has its exact command and <code className={styles.inlineCode}>.mcp.json</code> entry.{" "}
            <code className={styles.inlineCode}>claude mcp list</code> shows what&rsquo;s configured, and{" "}
            <code className={styles.inlineCode}>/mcp</code> inside a session shows what&rsquo;s actually connected.
          </p>
        </Section>

        <Section id="mcp-other-clients" title="Other MCP clients" defaultOpen={open("mcp-other-clients")} onOpen={onSectionOpen}>
          <p className={styles.pLead}>
            Add a new HTTP (streamable HTTP) server and paste the server URL from the server&rsquo;s page on {serversLink}.
          </p>
        </Section>

        <Section id="mcp-first-use" title="The first time you use a server" defaultOpen={open("mcp-first-use")} onOpen={onSectionOpen}>
          <p className={styles.pLead}>Whatever the client, your first tool call triggers one of these, depending on the server:</p>
          <ul className={styles.list} aria-label="What happens on first use">
            <li><strong>Nothing.</strong> Some servers need only your Cox sign-in.</li>
            <li>
              <strong>One consent click.</strong> You&rsquo;re sent to the vendor once to approve access. The platform then
              holds and refreshes that token, so there&rsquo;s nothing to paste or rotate.
            </li>
            <li>
              <strong>Your own API key, once.</strong> You&rsquo;re asked for your own key for that vendor. It&rsquo;s sealed to
              you, injected only into your own requests, and never stored in shared config.
            </li>
          </ul>
          <p className={styles.pSpaced}>Credentials are always per person. Two people using the same server each use their own access.</p>
        </Section>

        <Section id="mcp-managing" title="Managing or revoking access" defaultOpen={open("mcp-managing")} onOpen={onSectionOpen}>
          {myConnections ? (
            <>
              <p className={styles.pLead}>
                {connectionsLink} shows everything the platform holds for you, in two tabs:
              </p>
              <ul className={styles.list} aria-label="The My Connections tabs">
                <li>
                  <strong>Credentials:</strong> one entry per server you&rsquo;ve given an upstream credential for, tagged{" "}
                  <code className={styles.inlineCode}>oauth</code> or <code className={styles.inlineCode}>api_key</code>, and
                  Active or Expired
                </li>
                <li>
                  <strong>Consents:</strong> one entry per client app you&rsquo;ve authorized. This is where the Allow you
                  clicked in Claude Desktop ends up
                </li>
              </ul>
              <p className={styles.pSpaced}>
                A server can appear more than once under Consents: Claude Desktop, Claude Code and other clients each register
                separately, so revoking one doesn&rsquo;t affect the others. There&rsquo;s no &ldquo;replace&rdquo;: revoke,
                then use the server again to be asked again.
              </p>
            </>
          ) : (
            <>
              <p className={styles.pLead}>
                The MCP Auth Gateway keeps what it holds for you in two lists: <strong>Credentials</strong> (one per server
                you&rsquo;ve given an upstream credential for) and <strong>Consents</strong> (one per client app you&rsquo;ve
                authorized; this is where the Allow you clicked in Claude Desktop ends up).
              </p>
              <p className={styles.pFlush}>
                A server can appear more than once under Consents: Claude Desktop, Claude Code and other clients each register
                separately, so revoking one doesn&rsquo;t affect the others. There&rsquo;s no &ldquo;replace&rdquo;: revoke, then
                use the server again to be asked again.
              </p>
            </>
          )}
        </Section>

        <Section id="mcp-troubleshooting" title="If something isn't working" defaultOpen={open("mcp-troubleshooting")} onOpen={onSectionOpen}>
          <ul className={styles.list} aria-label="Common problems">
            <li>
              <strong>A tool call fails with an authorization error, having worked before.</strong> The stored credential has
              most likely expired.{" "}
              {myConnections ? (
                <>Revoke it in {connectionsLink} and use the server again to be re-prompted.</>
              ) : (
                <>Revoke it and use the server again to be asked again.</>
              )}
            </li>
            <li>
              <strong>The connector is enabled but no tools appear.</strong> The client caches the tool list; restart it. In
              Claude Code, <code className={styles.inlineCode}>claude mcp list</code> shows whether the server is reachable.
            </li>
            <li>
              <strong>Everything fails at once.</strong> That&rsquo;s more likely the platform than your setup. Ask in {slack}{" "}
              rather than re-adding connectors.
            </li>
          </ul>
          <p className={styles.pSpaced}>
            To report a problem, <Ext href={MCP_BUG_URL}>file a bug</Ext> and choose CAI MCP as the product.
          </p>
        </Section>

        <Section id="mcp-request" title="Request a new MCP server" defaultOpen={open("mcp-request")} onOpen={onSectionOpen}>
          <p className={styles.pLead}>
            Need a system that isn&rsquo;t on the platform yet, and the vendor already runs an MCP server?{" "}
            <Ext href={MCP_REQUEST_URL}>Request it with the Onboard MCP proxy form ↗</Ext>. It asks for:
          </p>
          <ul className={styles.list} aria-label="What the request form asks for">
            <li>A short name for the connector (lowercase with hyphens, up to 23 characters)</li>
            <li>The vendor&rsquo;s full MCP URL (HTTPS)</li>
            <li>The title and description people will see in the catalog</li>
            <li>Optionally, a consent message shown when people authorize it</li>
          </ul>
          <p className={styles.pSpaced}>
            An admin reviews each request and posts its deploy status on the tracking issue. Filing it doesn&rsquo;t deploy
            anything. Building your own MCP server to host on the platform? See the platform team&rsquo;s{" "}
            <Ext href={MCP_ONBOARDING_URL}>onboarding guide ↗</Ext>.
          </p>
        </Section>
      </div>
    </>
  );
}
